import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/api";
import DrWeedyLogo from "../assets/DrWeedyLogo";

export default function ChangePasswordPage() {
  const { clearFirstLogin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isFromAccount = location.state?.from === '/account';

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const passwordChecks = {
    minLength: newPassword.length >= 8,
    hasUpper: /[A-Z]/.test(newPassword),
    hasLower: /[a-z]/.test(newPassword),
  };

  const allChecksPassed = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!allChecksPassed) {
      setError("A senha não atende aos requisitos mínimos");
      return;
    }

    if (!passwordsMatch) {
      setError("As senhas não coincidem");
      return;
    }

    setLoading(true);
    try {
      await apiService.updatePassword(currentPassword, newPassword);
      clearFirstLogin();
      if (isFromAccount) {
        navigate('/account');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro ao alterar senha";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-petrolio-dark via-[#0a1a21] to-[#04090a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-saluton/15 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-petrolio/10 via-transparent to-transparent" />

      <div className="w-full flex flex-col items-center justify-center px-4 py-16 relative z-10">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-6">
              <DrWeedyLogo width={80} height={80} />
            </div>
            <Typography
              variant="h4"
              sx={{
                color: "#ffffff",
                fontWeight: 700,
                marginBottom: "8px",
                fontFamily: "Oxanium, sans-serif",
              }}
            >
              {isFromAccount ? "Alterar Senha" : "Primeiro Acesso"}
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.7)",
                fontSize: "16px",
              }}
            >
              Defina uma nova senha para continuar
            </Typography>
          </div>

          <Paper
            elevation={8}
            sx={{
              background:
                "linear-gradient(135deg, rgba(26, 49, 60, 0.8) 0%, rgba(4, 9, 10, 0.9) 100%)",
              backdropFilter: "blur(20px)",
              border: "2px solid rgba(226, 114, 91, 0.2)",
              borderRadius: "32px",
              padding: "40px",
              boxShadow:
                "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(226, 114, 91, 0.1)",
            }}
          >
            {!isFromAccount && (
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  borderRadius: "16px",
                  background: "rgba(59, 130, 246, 0.15)",
                  border: "1px solid rgba(59, 130, 246, 0.3)",
                  color: "rgba(255,255,255,0.9)",
                  "& .MuiAlert-icon": { color: "#60a5fa" },
                }}
              >
                Este é seu primeiro acesso. Por favor, defina uma nova senha.
              </Alert>
            )}

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: "16px",
                  background: "rgba(225, 100, 100, 0.15)",
                  border: "1px solid rgba(225, 100, 100, 0.3)",
                  color: "#e16464",
                }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <TextField
                fullWidth
                type="password"
                label={isFromAccount ? "Senha Atual" : "Senha Temporária"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                disabled={loading}
                sx={inputSx}
              />
              <TextField
                fullWidth
                type="password"
                label="Nova Senha"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading}
                sx={inputSx}
              />
              <TextField
                fullWidth
                type="password"
                label="Confirmar Nova Senha"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                error={confirmPassword.length > 0 && !passwordsMatch}
                helperText={
                  confirmPassword.length > 0 && !passwordsMatch
                    ? "As senhas não coincidem"
                    : ""
                }
                sx={inputSx}
              />

              {newPassword.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "13px", mb: 1 }}>
                    A senha deve conter:
                  </Typography>
                  <CheckItem label="Mínimo de 8 caracteres" ok={passwordChecks.minLength} />
                  <CheckItem label="Uma letra maiúscula" ok={passwordChecks.hasUpper} />
                  <CheckItem label="Uma letra minúscula" ok={passwordChecks.hasLower} />
                </Box>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading || !allChecksPassed || !passwordsMatch}
                sx={{
                  mt: 1,
                  background: "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)",
                  borderRadius: "24px",
                  textTransform: "none",
                  fontSize: "18px",
                  fontWeight: 700,
                  padding: "16px 32px",
                  color: "#ffffff",
                  border: "2px solid rgba(226, 114, 91, 0.3)",
                  boxShadow: "0 8px 24px rgba(226, 114, 91, 0.4)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 32px rgba(226, 114, 91, 0.5)",
                    background: "linear-gradient(135deg, #CC3B2A 0%, #E2725B 100%)",
                  },
                  "&:disabled": {
                    background: "rgba(255,255,255,0.1)",
                    color: "rgba(255,255,255,0.4)",
                  },
                }}
              >
                {loading ? <CircularProgress size={28} sx={{ color: "#ffffff" }} /> : "Salvar Nova Senha"}
              </Button>
            </Box>
          </Paper>
        </div>
      </div>
    </div>
  );
}

function CheckItem({ label, ok }: { label: string; ok: boolean }) {
  return (
    <Typography
      sx={{
        fontSize: "13px",
        color: ok ? "#4ade80" : "rgba(255,255,255,0.5)",
      }}
    >
      {ok ? "✔" : "✖"} {label}
    </Typography>
  );
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "20px",
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "#ffffff",
    fontSize: "17px",
    "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    "&:hover fieldset": { borderColor: "rgba(226, 114, 91, 0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#E2725B", borderWidth: "2px" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)", fontSize: "16px" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#E2725B" },
  "& .MuiInputLabel-shrink": {
    backgroundColor: "#0f2a35",
    padding: "0 8px",
    borderRadius: "4px",
  },
  "& .MuiFormHelperText-root": { color: "#e16464" },
};
